/*
  Первая колонка шапки счёта: деньги и что даст суд — §3.3.4, §4.5.2.

  Заведена 25.09.2026 вместо плитки «Ожидаемый возврат». Плитка показывала
  итог гейта отдачи одной цифрой - «−1 071,91 ₽», - и владелец, открыв боевой
  счёт, прочитал её как «деньги не вернутся». Цифра верна, но отвечает на
  вопрос «что даст СУД»: пошлина 2 000 ₽ при долге 4 529,93 ₽ съедает выгоду, а
  должник при этом собирался платить сам.

  Поэтому итог больше не стоит сам по себе. Над ним - две полосы на ОДНОЙ шкале
  от нуля до суммы долга: сколько ожидаем вернуть и во что обойдётся суд. Видно
  не только «сколько», но и почему столько: под красной полосой перечислены
  статьи расхода, и главная из них - пошлина.

  🔴 Цвета ровно два, зелёный и красный, и у каждой полосы обязательна подпись
  со значением. Янтарный рядом с красным неразличим для дальтоника (проверено
  валидатором оформления), а полоса без подписи не читается вовсе.
*/

import { Link } from 'react-router-dom'

import { date, feeNote, monthShort, money } from '../api/format.js'
import InfoHint from './InfoHint.jsx'
import LaterAction from './LaterAction.jsx'

const SCALE_HINT =
  'Обе полосы стоят на одной шкале - от нуля до суммы долга. Ожидаемый возврат ' +
  'посчитан правилами гейта отдачи по взысканиям, похожим на это; разговор с ' +
  'должником в него не входит.'

const CALIBRATION_HINT = {
  yes: ' Правила откалиброваны итогами первой волны.',
  no: ' Правила ещё не откалиброваны первой волной: это оценка, а не обещание.',
}

const RETURN_HINT =
  'Ожидаемый возврат минус расходы на суд. Цифра говорит только о судебном ' +
  'пути: если должник заплатит сам, вернётся весь долг, и пошлина не ' +
  'понадобится вовсе.'

/*
  Приказ отменён (аудит 26.10.2026, расхождение 3; строки 289 и 301 реестра): у счёта два
  действия, и идут они по очереди - сначала иск по месяцам приказа, затем заявление по
  остальным месяцам (решение владельца 30.10.2026, вариант А). Обе полосы - у иска: возврат
  от цены его месяцев, пошлина с зачётом внесённой за приказ, как в блоке «Исковое
  заявление» на вкладке «Суд». Остаток долга назван отдельной строкой «Затем».

  Признак - цена иска в вердикте (`lawsuit_price`), а не стадия счёта (ревью 30.10.2026):
  у отмены без месяцев гейт считает по всему долгу, и подпись по стадии врала. По той же
  цене строится шкала полос: до 02.11.2026 у нашего приказа долг счёта бывал нулевым (его
  дело «в суде» вырезало свои месяцы, строка 303), и на шкале «от нуля до долга» полосы
  выходили пустыми; шкала от цены иска верна и теперь, когда месяцы приказа снова в долге.
*/
const CANCELLED_NOTE =
  'Приказ отменён, дальше иск. Обе полосы - у иска: шкала от нуля до его цены, возврат от ' +
  'цены месяцев приказа, пошлина с зачётом внесённой за него, как в блоке «Исковое ' +
  'заявление» на вкладке «Суд».'

export default function AccountMoney({ triage, accountId, debtPeriod = null, debtSplit = null }) {
  const verdict = triage.verdict
  const scaleHint = SCALE_HINT + CALIBRATION_HINT[verdict.is_calibrated ? 'yes' : 'no']

  return (
    <div className="summary__col">
      <div className="summary__head">
        <div className="summary__cap">Долг к взысканию</div>
        <DebtPeriodLink accountId={accountId} period={debtPeriod} />
      </div>
      <div className="money__value money__value--hero">{money(triage.claim_amount)}</div>
      <DebtSplit split={debtSplit} />
      <div className="money__note">{feeNote(verdict)}</div>
      <Limitation triage={triage} />

      {/* У счёта, исключённого из взыскания, расчёта «выгодно ли в суд» нет: гейт отдачи
          про банкротство не знает, и «судиться выгодно» рядом с «исключён» (аудит
          26.10.2026, расхождение 1) подсказывало бы подачу, которую нельзя делать. */}
      {triage.route === 'excluded' ? (
        <div className="muted">
          Подача по счёту остановлена: он исключён из взыскания. Причина - в блоке «Банкротство» на
          вкладке «Должники»; расчёта, выгодно ли идти в суд, пока нет.
        </div>
      ) : (
        <CourtScale triage={triage} verdict={verdict} scaleHint={scaleHint} />
      )}
    </div>
  )
}

function CourtScale({ triage, verdict, scaleHint }) {
  const lawsuit = Number(verdict.lawsuit_price) > 0
  const scale = lawsuit ? verdict.lawsuit_price : triage.claim_amount
  return (
    <div className="scale">
      <div className="summary__cap">
        Что будет, если идти в суд
        <InfoHint title="Как читать полосы" text={scaleHint} />
      </div>

      <Bar
        label="Ожидаем вернуть"
        amount={verdict.expected_recovery}
        claim={scale}
        tone="good"
      />
      <Bar
        label="Расходы на суд"
        amount={verdict.action_cost}
        claim={scale}
        tone="bad"
        items={verdict.cost_items}
      />
      {lawsuit ? <div className="muted">{CANCELLED_NOTE}</div> : null}
      <LaterAction verdict={verdict} />

      <div className="verdict">
        {/*
          Красный здесь означает то же, что везде в кабинете, - потерю денег.
          Положительная отдача остаётся нейтральной: выгода это норма, а не
          событие, и красить её зелёным значило бы спорить с полосой выше.
        */}
        <span
          className={`verdict__value${
            Number(verdict.expected_return) < 0 ? ' verdict__value--burn' : ''
          }`}
        >
          {money(verdict.expected_return)}
        </span>
        <InfoHint title="Что это значит" text={RETURN_HINT} />
        <span className="verdict__note">{verdict.band_title}</span>
      </div>
    </div>
  )
}

/*
  «Долг сформировался за …» - в правом верхнем углу колонки денег, ссылкой на
  страницу «Как сформировался долг» (решение владельца 05.10.2026).

  Период считает сервер тем же расчётом, что и саму страницу: надпись ведёт
  туда, и разные периоды на двух экранах читались бы как ошибка в расчёте.
  Нет долга - нет и надписи: ссылаться не на что.

  Период держится одним куском (`summary__when`): в узкой колонке надпись
  переносится перед ним, а не посреди «окт. 2024 - авг. 2026».
*/
function DebtPeriodLink({ accountId, period }) {
  if (!period) return null
  const since = monthShort(period.since)
  const until = monthShort(period.until)
  return (
    <Link className="summary__period" to={`/accounts/${accountId}/debt`}>
      Долг сформировался за{' '}
      <span className="summary__when">{since === until ? since : `${since} - ${until}`}</span>
    </Link>
  )
}

/*
  Из чего сложилась сумма к взысканию: основной долг и пени (решение владельца
  05.10.2026). Без этой строки 212 613,47 ₽ на счёте, где по отчёту 1С должны
  174 717,90 ₽, читались как ошибка: пени в сумму входят, а сказано это не было.

  Обе части считает сервер тем же расчётом, что сумму над ними, - кабинет их не
  складывает. Пени не посчитать (ряд ставок не покрывает дату) - так и сказано.
*/
function DebtSplit({ split }) {
  if (!split) return null
  return (
    <div className="money__note">
      долг {money(split.principal)} · пени{' '}
      {split.penalty === null ? 'уточняются' : money(split.penalty)}
    </div>
  )
}

/*
  Деньги, которые уходят по сроку исковой давности.

  🔴 Стоят прямо под суммой долга, а не в опорных датах: полоса давности словами
  («в пределах», «горит», «истёк») названа там сервером, но сумма и день, до
  которого ещё можно подать, - это деньги, и красным в кабинете обозначается
  ровно одно - потеря денег во времени.

  Строки нет вовсе, пока терять нечего: у счёта в пределах срока, по которому
  ничего не выпало, эта строка была бы «0,00 ₽ красным» - тревога на ровном
  месте.
*/
function Limitation({ triage }) {
  const burning = Number(triage.burning_amount)
  const expired = Number(triage.expired_amount)

  if (burning > 0) {
    const until = triage.burning_date ? ` до ${date(triage.burning_date)}` : ''
    return (
      <div className="money__note money__note--burn">
        Горит {money(triage.burning_amount)}
        {until}
      </div>
    )
  }
  if (expired > 0) {
    return (
      <div className="money__note money__note--burn">
        Уже выпало за срок {money(triage.expired_amount)}
      </div>
    )
  }
  return null
}

/*
  Одна полоса шкалы. Ширина - единственное, что считается на клиенте: это
  геометрия, а не деньги. Сами суммы приходят строками и показываются как есть
  (`api/format.js`).

  Долг нулевой или неизвестный - полоса пустая, а не во всю ширину: делить не
  на что, и растянутая полоса соврала бы о доле.
*/
function Bar({ label, amount, claim, tone, items = [] }) {
  const whole = Number(claim)
  const part = Number(amount)
  const share = whole > 0 && Number.isFinite(part) ? Math.min(100, (part / whole) * 100) : 0

  return (
    <div className="bar">
      <div className="bar__label">
        <span>{label}</span>
        <b className={tone === 'bad' ? 'bar__sum bar__sum--burn' : 'bar__sum'}>{money(amount)}</b>
      </div>
      <div className="bar__track">
        <div className={`bar__fill bar__fill--${tone}`} style={{ width: `${share}%` }} />
      </div>
      {/*
        Статьи расхода приходят с сервера готовыми подписями (§4.5.1). Пустой
        список - расчёт старше правки от 25.09.2026: тогда полоса остаётся без
        расшифровки, а не подписывается догадкой о том, из чего она сложилась.
      */}
      {items.length > 0 ? (
        <div className="bar__parts">
          {items.map((item) => `${item.title} ${money(item.amount)}`).join(' · ')}
        </div>
      ) : null}
    </div>
  )
}
